/**
 * "Group dining session" intent — preserved across the signup redirect for anonymous users.
 *
 * Mirrors pointsClaimIntent / restaurantSaveIntent / menuScanIntent: an anonymous participant on a
 * dinner session taps the signup CTA at the value peak ("save this pick / claim your spot"), we
 * stash this intent, route them through create-account, then BaseLayout consumes it post-signup.
 * Consuming the intent triggers an EARLY return in BaseLayout's post-auth resume, which is what
 * bypasses onboarding — exactly like the save / points flows. The guest-eater claim itself runs
 * unconditionally on signup (idempotent); this intent just carries the return route back to the
 * session.
 */
const PENDING_GROUP_SESSION_INTENT_KEY = 'pending_group_session_intent'

export interface PendingGroupSessionIntent {
  shareToken: string
  returnRoute: string
}

export function getPendingGroupSessionIntent(): PendingGroupSessionIntent | null {
  try {
    const raw = sessionStorage.getItem(PENDING_GROUP_SESSION_INTENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingGroupSessionIntent
    if (!parsed?.shareToken) return null
    return {
      shareToken: parsed.shareToken,
      returnRoute: parsed.returnRoute ?? `/dine/${parsed.shareToken}`,
    }
  } catch {
    return null
  }
}

export function setPendingGroupSessionIntent(
  intent: PendingGroupSessionIntent,
): void {
  try {
    sessionStorage.setItem(
      PENDING_GROUP_SESSION_INTENT_KEY,
      JSON.stringify(intent),
    )
  } catch {
    // ignore
  }
}

export function clearPendingGroupSessionIntent(): void {
  try {
    sessionStorage.removeItem(PENDING_GROUP_SESSION_INTENT_KEY)
  } catch {
    // ignore
  }
}
